// Shared transport authority and drag frame for ordinary walks and Force Move.
import { LINK_STATUS_IDS } from './compound-drag.js';
import { encounterCreatureField, inventoryField } from '../../shared/array-types.js';
import { statusItemIdOf } from './squeeze-at-rest.js';
import type { PlainWalkReaders } from './plain-walk-scope.js';
import type { PlainWalkCommitInput } from './plain-walk.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const idOf = (row: Row): string => upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? inventoryField(row, 'inventory_id'));
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

/** determine_speed_type: unknown or omitted preference uses walking. The caller supplies its field reader. */
export function moverSpeedVariable(preferred: unknown): string {
  const modes: Record<string, string> = {
    'B96061AE-3369-7E7A-B6A2-C7C8FBF2A23F': 'speed_jumping',
    'ED9810A9-3456-4D6B-BC5C-A1BAF5952B98': 'speed_swimming',
    'EDCBE2A8-CD3D-43DC-9B92-84A634BE12AE': 'speed_climbing',
    'F1D0A27A-43F1-4A4C-A5F8-ED9BD2D908D4': 'speed_flying',
    'ACFB413D-15A8-835B-C821-9E101B6C4F60': 'speed_burrowing',
    '3C96DE74-D9E6-4173-8671-989209A0BF97': 'teleport',
    'AE7D4EB3-DC36-BD47-978F-1DBCFE2B616A': 'speed_hovering',
  };
  return modes[upper(preferred)] ?? 'speed_walking';
}

/** Legacy transport redirect: Piloted moves its pilot; Riding moves the mount whose Ridden row it cast. */
export function moverOfRecord(encounter: Row, dragged: Row, read: Pick<PlainWalkReaders, 'statuses'>): { id: string; row: Row } {
  const creatures = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr
    : Object.values(record(encounter.encounter_creatures_arr) ?? {})).map(record).filter((row): row is Row => row !== null);
  const find = (id: string): Row | undefined => creatures.find(row => idOf(row) === id);
  const link = (status: Row, key: keyof typeof LINK_STATUS_IDS): boolean => upper(statusItemIdOf(status)) === LINK_STATUS_IDS[key];
  let moverId = idOf(dragged), current: Row | undefined = find(moverId) ?? dragged;
  for (let hop = 0; hop < 4 && current; hop++) {
    let next = '';
    const statuses = read.statuses(current);
    for (const status of statuses) if (link(status, 'piloted')) { next = upper(status.status_caster_id); break; }
    if (next === '' && statuses.some(s => link(s, 'riding'))) {
      const riderId = idOf(current);
      next = idOf(creatures.find(row => read.statuses(row).some(s => link(s, 'ridden') && upper(s.status_caster_id) === riderId)) ?? {});
    }
    if (next === '' || next === moverId || !UUID.test(next)) break;
    const candidate = find(next);
    if (!candidate) break;
    moverId = next;
    current = candidate;
  }
  return { id: moverId, row: current ?? dragged };
}

/** Translate every route vertex by the held rows' offset; retain bends and optional elevation coordinates. */
export function moverDragFrame(input: PlainWalkCommitInput, dragged: Row, mover: Row, read: Pick<PlainWalkReaders, 'position'>):
  Pick<PlainWalkCommitInput, 'origin' | 'destination' | 'route' | 'supportChain' | 'supportSurfaces' | 'authoredGroundDrop'> {
  const [dx, dy, dz] = read.position(dragged), [mx, my, mz] = read.position(mover);
  const offset = [mx - dx, my - dy, mz - dz];
  return {
    origin: { x: mx, y: my, z: mz },
    destination: { x: input.destination.x + offset[0]!, y: input.destination.y + offset[1]!, z: input.destination.z + offset[2]! },
    route: input.route.map(point => point.map((value, axis) => value + (offset[axis] ?? 0))),
    supportChain: input.supportChain.map(surface => {
      const row = record(surface);
      return row ? { ...row, z: Number(scalar(row.z)) + offset[2]! } : surface;
    }),
    supportSurfaces: input.supportSurfaces?.map(surface => ({ ...surface, z: surface.z + offset[2]!, footprint: {
      ...surface.footprint, minX: surface.footprint.minX + offset[0]!, maxX: surface.footprint.maxX + offset[0]!,
      minY: surface.footprint.minY + offset[1]!, maxY: surface.footprint.maxY + offset[1]!,
    } })),
    // A ground contact for an elevated passenger does not prove the mover's destination is ground.
    authoredGroundDrop: input.authoredGroundDrop && Math.abs(offset[2]!) <= 1e-6,
  };
}
